import { useState, useEffect, useRef, useMemo } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import {
  Archive,
  Trash2,
  Link as LinkIcon,
  UserPlus,
  ChevronDown,
  ChevronUp,
  Mail,
  Calendar,
  MapPin,
  Phone,
  Sparkles,
  Send,
  Loader2,
  Reply,
  X,
  MessageSquare,
  Info,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "@/hooks/use-toast";
import { RichTextEditor } from "../RichTextEditor";
import { TemplateSelector } from "../communication/TemplateSelector";
import { LinkInquiryModal } from "../communication/LinkInquiryModal";
import { CreateInquiryModal } from "../communication/CreateInquiryModal";
import { cn } from "@/lib/utils";
import type { EmailThread } from "./EmailThreadList";

interface Email {
  id: string;
  thread_id: string;
  from_email: string;
  to_email: string;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  direction: string;
  sent_at: string;
  read_at: string | null;
}

interface EmailDetailPanelProps {
  thread: EmailThread | null;
  onThreadUpdate: () => void;
  onThreadArchive: (threadId: string) => void;
  onThreadDelete: (threadId: string) => void;
}

export function EmailDetailPanel({
  thread,
  onThreadUpdate,
  onThreadArchive,
  onThreadDelete,
}: EmailDetailPanelProps) {
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(false);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [aiInstructions, setAiInstructions] = useState("");
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [linkInquiryOpen, setLinkInquiryOpen] = useState(false);
  const [createInquiryOpen, setCreateInquiryOpen] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<string[]>([]);
  const [replyOpen, setReplyOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const previousEmails = useMemo(() => emails.slice(0, -1), [emails]);
  const allExpanded = previousEmails.length > 0 && expandedMessages.length === previousEmails.length;

  const handleExpandAll = () => {
    if (allExpanded) {
      setExpandedMessages([]);
    } else {
      setExpandedMessages(previousEmails.map((email) => email.id));
    }
  };

  useEffect(() => {
    if (thread) {
      fetchEmails();
      markAsRead();
    } else {
      setEmails([]);
    }
  }, [thread?.id]);

  useEffect(() => {
    // Scroll to bottom when emails change
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [emails]);

  const fetchEmails = async () => {
    if (!thread) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("emails")
        .select("*")
        .eq("thread_id", thread.id)
        .order("sent_at", { ascending: true });

      if (error) throw error;
      setEmails(data || []);
    } catch (error) {
      console.error("Error fetching emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    if (!thread || thread.unread_count === 0) return;
    try {
      await supabase
        .from("email_threads")
        .update({ unread_count: 0 })
        .eq("id", thread.id);
      
      await supabase
        .from("emails")
        .update({ read_at: new Date().toISOString() })
        .eq("thread_id", thread.id)
        .is("read_at", null);
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!thread) return;
    try {
      const { error } = await supabase
        .from("email_threads")
        .update({ status: newStatus })
        .eq("id", thread.id);

      if (error) throw error;
      toast({ title: "Status updated" });
      onThreadUpdate();
    } catch (error) {
      console.error("Error updating status:", error);
      toast({ title: "Failed to update status", variant: "destructive" });
    }
  };

  const handleGenerateReply = async () => {
    if (!thread) return;
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-email-reply", {
        body: {
          threadId: thread.id,
          customInstructions: aiInstructions || undefined,
        },
      });

      if (error) throw error;
      if (data?.reply) {
        setReplyBody(data.reply);
        toast({ title: "Reply generated" });
      }
    } catch (error: any) {
      console.error("Error generating reply:", error);
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate AI reply",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSendReply = async () => {
    if (!thread || !replyBody.trim()) return;
    setSending(true);
    try {
      // Avoid duplicate "Re:" prefix
      const replySubject = thread.subject.toLowerCase().startsWith("re:") 
        ? thread.subject 
        : `Re: ${thread.subject}`;
      
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          threadId: thread.id,
          toEmail: thread.inquiry?.email || thread.sender_email,
          subject: replySubject,
          bodyHtml: replyBody,
        },
      });

      if (error) throw error;
      toast({ title: "Email sent" });
      setReplyBody("");
      setAiInstructions("");
      setReplyOpen(false);
      fetchEmails();
      onThreadUpdate();
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast({
        title: "Failed to send",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleInquiryLinked = () => {
    setLinkInquiryOpen(false);
    setCreateInquiryOpen(false);
    onThreadUpdate();
  };

  if (!thread) {
    return (
      <div className="flex-1 flex items-center justify-center bg-muted/30">
        <div className="text-center text-muted-foreground">
          <Mail className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">Select a conversation to view</p>
        </div>
      </div>
    );
  }

  const inquiry = thread.inquiry;
  
  // Get display name - prioritize inquiry name when linked
  const displayName = inquiry 
    ? `${inquiry.first_name} ${inquiry.last_name}`.trim() 
    : (thread.sender_name || thread.sender_email || "Unknown Sender");
  
  const displayInitial = inquiry 
    ? (inquiry.first_name?.[0] || "U").toUpperCase()
    : (thread.sender_name || thread.sender_email || "U")[0].toUpperCase();

  return (
    <div className="flex-1 flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-primary font-semibold">
              {displayInitial}
            </span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold truncate">{displayName}</h2>
              {inquiry && (
                <Badge variant="secondary" className="text-xs shrink-0">Linked</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground truncate">{thread.subject}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {inquiry && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setContactDrawerOpen(true)}
              className="gap-1.5"
            >
              <Info className="h-4 w-4" />
              <span className="hidden sm:inline">Contact Info</span>
            </Button>
          )}
          
          <Select value={thread.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="awaiting_reply">Awaiting Reply</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          {!thread.inquiry_id && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLinkInquiryOpen(true)}
              >
                <LinkIcon className="h-4 w-4 mr-1" />
                Link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateInquiryOpen(true)}
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Create
              </Button>
            </>
          )}

          <Button
            variant="outline"
            size="icon"
            onClick={() => onThreadArchive(thread.id)}
          >
            <Archive className="h-4 w-4" />
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="icon">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this thread?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the thread and all emails. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onThreadDelete(thread.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Email Thread - Full Width Now */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4 max-w-3xl mx-auto">
                {/* Previous Messages Accordion */}
                {previousEmails.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-muted-foreground">
                          Previous messages ({previousEmails.length})
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleExpandAll}
                        className="text-xs h-7"
                      >
                        {allExpanded ? (
                          <>
                            <ChevronUp className="h-3 w-3 mr-1" />
                            Collapse All
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3 w-3 mr-1" />
                            Expand All
                          </>
                        )}
                      </Button>
                    </div>
                    <Accordion
                      type="multiple"
                      value={expandedMessages}
                      onValueChange={setExpandedMessages}
                      className="w-full space-y-2"
                    >
                      {previousEmails.map((email) => {
                        const preview = (email.body_text || email.body_html?.replace(/<[^>]*>/g, '') || '').slice(0, 80);
                        return (
                          <AccordionItem
                            key={email.id}
                            value={email.id}
                            className={cn(
                              "border rounded-lg overflow-hidden",
                              email.direction === "outbound"
                                ? "bg-primary/5 border-primary/20"
                                : "bg-background border-border"
                            )}
                          >
                            <AccordionTrigger className="px-4 py-3 hover:no-underline">
                              <div className="flex items-center justify-between w-full mr-2 text-left">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-medium text-sm">
                                    {email.direction === "outbound" ? "You" : displayName}
                                  </span>
                                  <span className="text-xs text-muted-foreground hidden sm:inline">
                                    {formatDistanceToNow(new Date(email.sent_at), { addSuffix: true })}
                                  </span>
                                </div>
                                <span className="text-xs text-muted-foreground truncate max-w-[200px] hidden md:inline">
                                  {preview}{preview.length >= 80 ? '...' : ''}
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="text-xs text-muted-foreground mb-2">
                                {format(new Date(email.sent_at), "MMM d, yyyy 'at' h:mm a")}
                              </div>
                              <div
                                className="prose prose-sm max-w-none dark:prose-invert"
                                dangerouslySetInnerHTML={{ __html: email.body_html || email.body_text || "" }}
                              />
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </div>
                )}

                {/* Latest Email - Primary Focus */}
                {emails.length > 0 && (
                  <div className="space-y-3">
                    {previousEmails.length > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs text-muted-foreground font-medium">Latest message</span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "rounded-lg border p-5",
                        emails[emails.length - 1].direction === "outbound"
                          ? "bg-primary/5 border-primary/20"
                          : "bg-card border-border"
                      )}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className="font-semibold">
                            {emails[emails.length - 1].direction === "outbound" ? "You" : displayName}
                          </span>
                          {emails[emails.length - 1].direction === "inbound" && thread.sender_email && (
                            <p className="text-sm text-muted-foreground">{thread.sender_email}</p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(emails[emails.length - 1].sent_at), "MMM d, yyyy 'at' h:mm a")}
                        </span>
                      </div>
                      <div
                        className="prose prose-sm max-w-none dark:prose-invert"
                        dangerouslySetInnerHTML={{ __html: emails[emails.length - 1].body_html || emails[emails.length - 1].body_text || "" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Reply Section - Collapsible */}
          <div className="border-t border-border bg-card">
            {!replyOpen ? (
              <div className="p-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setReplyOpen(true)}
                >
                  <Reply className="h-4 w-4 mr-2" />
                  Reply to this email
                </Button>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Compose Reply</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setReplyOpen(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  <TemplateSelector
                    inquiry={inquiry}
                    onSelectTemplate={(subject, body) => {
                      setReplyBody(body);
                      toast({ title: "Template applied" });
                    }}
                  />
                  
                  <div className="flex-1">
                    <Textarea
                      placeholder="AI instructions (optional): e.g., 'Focus on pricing details'"
                      value={aiInstructions}
                      onChange={(e) => setAiInstructions(e.target.value)}
                      className="min-h-[40px] resize-none text-sm"
                      rows={1}
                    />
                  </div>
                  
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleGenerateReply}
                    disabled={generating}
                  >
                    {generating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-1" />
                        Generate
                      </>
                    )}
                  </Button>
                </div>

                <div className="max-h-[200px] overflow-y-auto border rounded-md">
                  <RichTextEditor
                    value={replyBody}
                    onChange={setReplyBody}
                    placeholder="Write your reply..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-border mt-2">
                  <Button
                    variant="outline"
                    onClick={() => setReplyOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendReply}
                    disabled={sending || !replyBody.trim()}
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <LinkInquiryModal
        open={linkInquiryOpen}
        onOpenChange={setLinkInquiryOpen}
        threadId={thread.id}
        onLinked={handleInquiryLinked}
      />

      <CreateInquiryModal
        open={createInquiryOpen}
        onOpenChange={setCreateInquiryOpen}
        threadId={thread.id}
        senderName={thread.sender_name}
        senderEmail={thread.sender_email}
        onCreated={handleInquiryLinked}
      />

      {/* Contact Details Drawer */}
      <Drawer open={contactDrawerOpen} onOpenChange={setContactDrawerOpen}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>Contact Details</DrawerTitle>
            <DrawerDescription>
              Linked inquiry information
            </DrawerDescription>
          </DrawerHeader>
          {inquiry && (
            <div className="px-4 pb-6 space-y-4">
              {/* Name and Email */}
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-semibold text-lg">
                    {inquiry.first_name?.[0]?.toUpperCase() || "U"}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-lg">{inquiry.first_name} {inquiry.last_name}</p>
                  <a href={`mailto:${inquiry.email}`} className="text-sm text-muted-foreground hover:text-primary">
                    {inquiry.email}
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Phone */}
                {inquiry.phone && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <a href={`tel:${inquiry.phone}`} className="font-medium hover:text-primary">
                        {inquiry.phone}
                      </a>
                    </div>
                  </div>
                )}

                {/* Wedding Date */}
                {inquiry.wedding_date && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Wedding Date</p>
                      <p className="font-medium">{format(new Date(inquiry.wedding_date), "MMM d, yyyy")}</p>
                    </div>
                  </div>
                )}

                {/* Venue */}
                {inquiry.venue && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Venue</p>
                      <p className="font-medium">{inquiry.venue}</p>
                    </div>
                  </div>
                )}

                {/* Budget */}
                {inquiry.budget && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Budget</p>
                      <p className="font-medium">{inquiry.budget}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Services */}
              {inquiry.services && inquiry.services.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Services Interested In</p>
                  <div className="flex flex-wrap gap-2">
                    {inquiry.services.map((service) => (
                      <Badge key={service} variant="secondary">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
